// Upload + Pre-flight surface, in the v2 Magic Patterns language.
//
// Tate's spec (locked, day 0.1):
//   - No dropzone box. Centered editorial-serif invitation + tracked-caps
//     UPLOAD · PASTE · SCAN as plain text affordances, not pills.
//   - Page chooser: quiet vertical run of page/scene markers, taps select
//     w/ gold hairline mark.
//   - Pre-flight ask: the Presence poses one calm question — "Any page
//     that came in rough?" — tapping flags it for the correction pass.
//   - The wait narrates itself: stage events in tracked caps over cream.
//     No spinner.
//
// Cael's strings (locked):
//   - Invitation: "What are you working on?"
//   - Affordances: UPLOAD · PASTE · SCAN
//   - Page chooser: "Which pages?"
//   - Pre-flight: "Any page that came in rough?"
//   - Flag confirm: "Flagged. We'll give that page a closer read."
//   - Loader stages: Reading scene N · Recognizing beats · Absorbing the arcs
import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  getParseScriptJob, InspectResult, ParseScriptStage,
  postInspect, postParseScript, postParseScriptJob,
} from "../api/client";
import { useArc } from "../state/arc";

const POLL_INTERVAL_MS = 2500;

type Phase = "invite" | "inspecting" | "pre-flight" | "parsing" | "done-warn";

export default function ScriptUpload() {
  const navigate = useNavigate();
  const { update } = useArc();
  const fileRef = useRef<HTMLInputElement | null>(null);
  const [phase, setPhase] = useState<Phase>("invite");
  const [file, setFile] = useState<File | null>(null);
  const [inspect, setInspect] = useState<InspectResult | null>(null);
  const [flagged, setFlagged] = useState<Set<number>>(new Set());
  const [stage, setStage] = useState<ParseScriptStage | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [warnings, setWarnings] = useState<string[]>([]);
  const pollTimer = useRef<number | null>(null);

  useEffect(() => {
    return () => {
      if (pollTimer.current !== null) window.clearTimeout(pollTimer.current);
    };
  }, []);

  function openPicker() { fileRef.current?.click(); }

  async function onFileChosen(f: File) {
    setFile(f);
    setError(null);
    setWarnings([]);
    setStage(null);
    setFlagged(new Set());
    setPhase("inspecting");
    try {
      const result = await postInspect(f);
      setInspect(result);
      if (result.condition === "unsupported" || result.condition === "garbled") {
        setError(result.warnings.join(" ") || "We couldn't read that file.");
        setPhase("invite");
        return;
      }
      setPhase("pre-flight");
    } catch (e: any) {
      setError(e?.message ?? "Couldn't read that file.");
      setPhase("invite");
    }
  }

  function togglePageFlag(page: number) {
    setFlagged((prev) => {
      const next = new Set(prev);
      if (next.has(page)) next.delete(page); else next.add(page);
      return next;
    });
  }

  async function pollJob(jobId: string): Promise<void> {
    return new Promise((resolve, reject) => {
      const tick = async () => {
        try {
          const job = await getParseScriptJob(jobId);
          setStage(job.stage);
          if (job.status === "done" && job.result) {
            const res: any = job.result;
            if (!res.project?.cues?.length) {
              setWarnings(res.warnings ?? []);
              setPhase("done-warn");
              resolve();
              return;
            }
            update({
              parsedProject: res.project,
              parsedScript: res.parsed_script ?? null,
            });
            navigate("/character");
            resolve();
            return;
          }
          if (job.status === "error") {
            setError(job.error || "Parse failed.");
            setPhase("pre-flight");
            reject(new Error(job.error || "Parse failed."));
            return;
          }
          pollTimer.current = window.setTimeout(tick, POLL_INTERVAL_MS);
        } catch (e: any) {
          setError(e?.message ?? "Polling failed.");
          setPhase("pre-flight");
          reject(e);
        }
      };
      tick();
    });
  }

  async function startParse() {
    if (!file || !inspect) return;
    setError(null);
    setPhase("parsing");
    try {
      if (inspect.recommended_path === "/parse-script-job") {
        const { job_id } = await postParseScriptJob(file);
        await pollJob(job_id);
      } else {
        const res = await postParseScript(file);
        if (!res.project.cues.length) {
          setWarnings(res.warnings);
          setPhase("done-warn");
          return;
        }
        update({
          parsedProject: res.project,
          parsedScript: (res as any).parsed_script ?? null,
        });
        navigate("/character");
      }
    } catch (e: any) {
      setError(e?.message ?? "Parse failed.");
      setPhase("pre-flight");
    }
  }

  // ----- Render ---------------------------------------------------------

  if (phase === "invite") {
    return (
      <div className="v2">
        <div style={{ minHeight: "62vh", display: "flex", flexDirection: "column", justifyContent: "center" }}>
          <div className="v2-presence-small" aria-hidden />
          <p className="v2-invitation">What are you working on?</p>
          <div style={{ marginTop: "3rem", display: "flex", justifyContent: "center", gap: "2.6rem", flexWrap: "wrap" }}>
            <button className="v2-affordance" onClick={openPicker}>Upload</button>
            <button className="v2-affordance" disabled>Paste</button>
            <button className="v2-affordance" disabled>Scan</button>
          </div>
          <input
            ref={fileRef} type="file" hidden
            accept=".pdf,.docx,.rtf,.txt,.md,.markdown"
            onChange={(e) => e.target.files?.[0] && onFileChosen(e.target.files[0])}
          />
          {error && (
            <p className="v2-stage-event-detail" style={{ marginTop: "2rem", color: "#B83E3E" }}>
              {error}
            </p>
          )}
        </div>
      </div>
    );
  }

  if (phase === "inspecting") {
    return (
      <div className="v2">
        <div style={{ minHeight: "60vh", display: "flex", flexDirection: "column", justifyContent: "center" }}>
          <div className="v2-presence-small" aria-hidden />
          <p className="v2-stage-event">Reading the script</p>
        </div>
      </div>
    );
  }

  if (phase === "pre-flight" && inspect) {
    const pages = Array.from({ length: inspect.page_count }, (_, i) => i + 1);
    const niceCondition = ({
      "text-extractable": "clean text",
      "clean-scan":       "a clean scan",
      "dense-scan":       "a dense scan",
      "image-only-no-text": "an image-only PDF",
    } as Record<string, string>)[inspect.condition] || inspect.condition;
    const mins = Math.max(1, Math.round(inspect.est_seconds / 60));
    return (
      <div className="v2">
        <p className="v2-tracked-caps" style={{ textAlign: "center" }}>{file?.name}</p>
        <h2 style={{ textAlign: "center" }}>
          Looks like {niceCondition}. About {mins} minute{mins === 1 ? "" : "s"}.
        </h2>
        <p className="v2-prompt" style={{ textAlign: "center", marginTop: "0.6rem" }}>
          Any page that came in rough?
        </p>

        <div className="v2-page-chooser">
          {pages.map((p) => {
            const sel = flagged.has(p);
            return (
              <div
                key={p}
                className={"v2-page-row" + (sel ? " is-selected" : "")}
                onClick={() => togglePageFlag(p)}
                role="button"
              >
                <span className="v2-page-num">Page {p}</span>
                <span className="v2-page-excerpt">{sel ? "We'll give it a closer read." : ""}</span>
                <span className="v2-flag">{sel ? "flagged" : ""}</span>
              </div>
            );
          })}
        </div>

        <hr className="v2-hairline" />
        <div style={{ display: "flex", justifyContent: "center", gap: "2.5rem", marginTop: "1.5rem" }}>
          <button className="v2-affordance" onClick={() => setPhase("invite")}>Cancel</button>
          <button className="v2-affordance" onClick={startParse}>Begin</button>
        </div>
        {error && (
          <p className="v2-stage-event-detail" style={{ marginTop: "1.5rem", color: "#B83E3E" }}>
            {error}
          </p>
        )}
      </div>
    );
  }

  if (phase === "parsing") {
    const label = stage?.label || "Reading the script";
    const sub = stage?.total && stage.total > 0
      ? `${Math.round((stage.index / stage.total) * 100)}% — leave this tab open`
      : "leave this tab open";
    return (
      <div className="v2">
        <div style={{ minHeight: "60vh", display: "flex", flexDirection: "column", justifyContent: "center" }}>
          <div className="v2-presence-small" aria-hidden />
          <p className="v2-stage-event">{label}</p>
          <p className="v2-stage-event-detail">{sub}</p>
        </div>
      </div>
    );
  }

  // done-warn — parse returned 0 cues
  return (
    <div className="v2">
      <h2 style={{ textAlign: "center" }}>Hmm.</h2>
      <p className="v2-prompt" style={{ textAlign: "center" }}>
        We read the file but didn't find dialogue. The parser said:
      </p>
      {warnings.map((w, i) => (
        <p key={i} className="v2-stage-event-detail" style={{ textAlign: "center" }}>· {w}</p>
      ))}
      <div style={{ display: "flex", justifyContent: "center", marginTop: "2rem" }}>
        <button className="v2-affordance" onClick={() => setPhase("invite")}>Try another</button>
      </div>
    </div>
  );
}
